import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Health-posture surface (T-0072 / E07-F02).
 *
 * The engagement overview hero (T-0097) needs two reads:
 *
 *   - Overall posture rollup (engagement-level)
 *       GET /api/v1/engagements/:id/health/overall-posture
 *     Feeds the hero's posture pill. The API returns
 *     `green | yellow_divergent | red | grey` (the verification-rollup
 *     palette); we translate to the `<PostureChip>` vocabulary
 *     (`healthy | at-risk | critical | unassessed`) at the call site.
 *
 *   - Per-domain latest assessments (used by the health-posture-grid
 *     subsection below the facts strip). Same endpoint — `per_domain`
 *     carries `name`, `display_order`, and `latest_rollup_color` for
 *     each active HealthDomain.
 *
 * The R6 invariant (posture is computed, never AI-produced) lives in
 * services/domain-assessments.ts → getOverallPosture. We just read it.
 */

/**
 * Verification-rollup palette. The four legacy values (green /
 * yellow_divergent / red / grey) come from the per-engagement
 * `overall-posture` endpoint (T-0072). The `unassessed` variant is
 * added for the portfolio composite (T-0081 / PR #128), where it
 * distinguishes an engagement with NO committed assessments from one
 * with a `grey` (still-being-assessed) rollup. Per-engagement reads
 * never return `unassessed` today; the union is widened so the shared
 * type can stand in for both surfaces.
 */
export const RollupColorSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey', 'unassessed']);
export type RollupColor = z.infer<typeof RollupColorSchema>;

export const DomainRatingSchema = z.enum(['healthy', 'at_risk', 'critical']);
export type DomainRating = z.infer<typeof DomainRatingSchema>;

export const OverallPostureDomainSchema = z.object({
  domain_id: z.string().uuid(),
  name: z.string(),
  display_order: z.number(),
  latest_rollup_color: RollupColorSchema.nullable(),
  latest_rating: DomainRatingSchema.nullable(),
  latest_assessment_id: z.string().uuid().nullable(),
  // When the latest committed assessment was confirmed. Optional so an
  // older API build that predates the field still parses.
  latest_confirmed_at: z.string().nullable().optional(),
});
export type OverallPostureDomain = z.infer<typeof OverallPostureDomainSchema>;

export const OverallPostureSchema = z.object({
  overall_rollup: RollupColorSchema,
  healthy_count: z.number(),
  total_active_domains: z.number(),
  is_fully_healthy: z.boolean(),
  per_domain: z.array(OverallPostureDomainSchema),
});
export type OverallPosture = z.infer<typeof OverallPostureSchema>;

export function getOverallPosture(engagementId: string): Promise<OverallPosture> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/overall-posture`,
    OverallPostureSchema,
  );
}

export function useOverallPostureQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['health', 'overall-posture', engagementId] as const,
    queryFn: () => getOverallPosture(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

/**
 * Translate the verification-rollup color enum into the `<PostureChip>`
 * status vocabulary. The two enums are intentionally separate — the
 * rollup palette is a deterministic truth-table over (vendor_claim,
 * avero_verification); the posture-chip status is the visual label used
 * across the UI (load-bearing primitive, README §6).
 *
 *   green             → healthy
 *   yellow_divergent  → at-risk  (vendor-claim and avero disagree)
 *   red               → critical
 *   grey              → unassessed
 */
export function rollupColorToPostureStatus(
  c: RollupColor | null | undefined,
): 'healthy' | 'at-risk' | 'critical' | 'unassessed' {
  switch (c) {
    case 'green':
      return 'healthy';
    case 'yellow_divergent':
      return 'at-risk';
    case 'red':
      return 'critical';
    case 'grey':
    case 'unassessed':
    case null:
    case undefined:
      return 'unassessed';
  }
}
